/**
 * STJ Confetti
 * A single-canvas confetti burst, fired on successful add-to-cart.
 *
 * Why canvas rather than DOM nodes: layout/theme.liquid runs a body-wide
 * MutationObserver (paintCartBars) and snippets/gokwik.liquid runs another that
 * re-enables the checkout button 700ms after ANY mutation. Appending 50 confetti
 * divs would trip both, 50 times. One canvas inserts one node.
 *
 * Exposes: window.STJConfetti.burst(options)
 *   options.origin        'cart' | 'top' | 'bottom' | {x, y}  (x/y are 0-1 fractions)
 *   options.particleCount number
 *   options.duration      ms
 *   options.spread        degrees
 *   options.colors        array of css colours
 *
 * Settings come from window.STJConfettiSettings, emitted by layout/theme.liquid.
 */
(function () {
  if (window.STJConfetti) return;

  var CFG = window.STJConfettiSettings || {};
  var enabled = CFG.enabled !== false;

  var DEFAULTS = {
    particleCount: CFG.particleCount || 120,
    duration: CFG.duration || 2600,
    spread: CFG.spread || 85,
    origin: CFG.origin || 'cart',
    colors: (CFG.colors && CFG.colors.length ? CFG.colors : ['#b88746', '#d4af37', '#f3d993', '#7c0312', '#a01224', '#ffffff', '#e8a838'])
  };

  var canvas = null;
  var ctx = null;
  var particles = [];
  var rafId = null;
  var dpr = Math.min(window.devicePixelRatio || 1, 2);

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function ensureCanvas() {
    if (canvas) return canvas;
    canvas = document.createElement('canvas');
    canvas.className = 'stj-confetti-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100vw;height:100vh;' +
      'pointer-events:none;z-index:2147483000;';
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    resize();
    window.addEventListener('resize', resize);
    return canvas;
  }

  function resize() {
    if (!canvas) return;
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function teardown() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    window.removeEventListener('resize', resize);
    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    canvas = null;
    ctx = null;
    particles = [];
  }

  /** Resolve the burst origin to viewport pixels. */
  function resolveOrigin(origin) {
    var w = window.innerWidth;
    var h = window.innerHeight;

    if (origin && typeof origin === 'object' && typeof origin.x === 'number') {
      return { x: origin.x * w, y: origin.y * h };
    }

    // Cart drawer target: on desktop the drawer is 480px wide fixed on the right side.
    var isDesktop = w >= 750;
    var innerPanel = document.querySelector('cart-drawer .drawer__inner, #CartDrawer .drawer__inner, .drawer__inner');
    var drawerRoot = document.querySelector('cart-drawer');
    var isDrawerOpen = drawerRoot && (drawerRoot.classList.contains('active') || drawerRoot.classList.contains('animate'));

    if (origin === 'drawer' || origin === 'cart' || isDrawerOpen) {
      if (isDesktop) {
        // Desktop cart drawer is on the right side (width ~450px)
        var drawerCenter = w - 225;
        if (innerPanel) {
          var ir = innerPanel.getBoundingClientRect();
          if (ir.width > 100 && ir.left > w * 0.4) {
            drawerCenter = ir.left + ir.width * 0.5;
          }
        }
        return { x: drawerCenter, y: Math.max(120, h * 0.28) };
      } else {
        // Mobile drawer
        return { x: w * 0.5, y: Math.max(100, h * 0.25) };
      }
    }

    if (origin === 'top') return { x: w / 2, y: h * 0.12 };
    if (origin === 'bottom') return { x: w / 2, y: h * 0.92 };

    // Default for desktop: upper-right where cart drawer slides in
    if (isDesktop) {
      return { x: w - 225, y: Math.max(120, h * 0.28) };
    }
    return { x: w * 0.5, y: h * 0.25 };
  }

  function spawn(opts) {
    var origin = resolveOrigin(opts.origin);
    var count = opts.particleCount;
    var spreadRad = (opts.spread * Math.PI) / 180;
    var baseAngle = -Math.PI / 2; // Fan upward then fall with gravity

    for (var i = 0; i < count; i++) {
      var angle = baseAngle + (Math.random() - 0.5) * spreadRad;
      var speed = 6 + Math.random() * 9;
      var isRibbon = Math.random() < 0.45;
      var w = isRibbon ? (7 + Math.random() * 6) : (9 + Math.random() * 8);
      var h = isRibbon ? (14 + Math.random() * 12) : (9 + Math.random() * 8);

      particles.push({
        x: origin.x + (Math.random() - 0.5) * 40,
        y: origin.y + (Math.random() - 0.5) * 20,
        vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 3,
        vy: Math.sin(angle) * speed,
        w: w,
        h: h,
        color: opts.colors[(Math.random() * opts.colors.length) | 0],
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.25,
        round: !isRibbon && Math.random() < 0.3,
        born: performance.now(),
        life: opts.duration * (0.7 + Math.random() * 0.3)
      });
    }
  }

  function frame() {
    if (!ctx || !canvas) return;

    var now = performance.now();
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    var alive = 0;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      var age = now - p.born;
      if (age > p.life) continue;

      alive++;
      p.vy += 0.22; // gravity
      p.vx *= 0.995; // drag
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;

      var fade = 1 - age / p.life;
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, fade * 1.4));
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      }
      ctx.restore();
    }

    if (alive > 0) {
      rafId = requestAnimationFrame(frame);
    } else {
      teardown();
    }
  }

  var lastBurst = 0;

  function burst(options) {
    if (!enabled) return;
    if (prefersReducedMotion()) return;
    if (!document.body) return;

    // Guard against two add-paths firing for the same action.
    var now = Date.now();
    if (now - lastBurst < 250) return;
    lastBurst = now;

    var opts = {
      particleCount: DEFAULTS.particleCount,
      duration: DEFAULTS.duration,
      spread: DEFAULTS.spread,
      origin: DEFAULTS.origin,
      colors: DEFAULTS.colors
    };
    if (options) {
      for (var k in options) {
        if (Object.prototype.hasOwnProperty.call(options, k) && options[k] != null) opts[k] = options[k];
      }
    }

    ensureCanvas();
    // Cap total particles so rapid adds cannot compound into a jank storm.
    if (particles.length > 400) particles.splice(0, particles.length - 400);
    spawn(opts);

    if (!rafId) rafId = requestAnimationFrame(frame);
  }

  window.STJConfetti = { burst: burst };

  /**
   * Fire on add-to-cart.
   *
   * cartUpdate is published with four different payload shapes across this theme,
   * and it fires for quantity changes too. Only treat it as an "add" when the
   * source is one of the known add paths (or absent, which is the cart-drawer
   * FBT shape). 'cart-items' and 'quick-order-list' are quantity/remove.
   */
  var ADD_SOURCES = ['product-form', 'quick-add', 'mobile-sticky-atc'];
  var IGNORED_SOURCES = ['cart-items', 'quick-order-list', 'discount-auto-apply'];

  function onCartUpdate(event) {
    if (!event) return;
    var source = event.source;
    if (source && IGNORED_SOURCES.indexOf(source) !== -1) return;
    if (source && ADD_SOURCES.indexOf(source) === -1) return;
    burst();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      }
    });
  }
})();
